import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/base/Breadcrumb";
import VideoCard from "@/components/feature/VideoCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import SkeletonCard from "@/components/base/SkeletonCard";
import FilterBar from "@/pages/category/components/FilterBar";
import { categories } from "@/mocks/categories";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useSimulatedLoading } from "@/hooks/useSimulatedLoading";
import { getCategory, queryVideos, type SortKey } from "@/utils/video";

const PAGE_SIZE = 8;

export default function CategoryPage() {
  const { type = "all" } = useParams();
  const category = getCategory(type);

  const [year, setYear] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);

  const categoryName = category?.name ?? "未找到分类";

  usePageMeta(
    `${categoryName} - 色色视频`,
    category?.description ??
      "该分类不存在或已调整，可返回首页浏览其他视频分类内容。",
    `${categoryName},色色视频,在线视频,高清视频,${categoryName}视频`,
  );

  const results = useMemo(
    () =>
      queryVideos({
        category: category?.key ?? "all",
        year,
        region,
        sort,
      }),
    [category?.key, year, region, sort],
  );

  const loading = useSimulatedLoading(`${type}-${year}-${region}-${sort}-${page}`, 300);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleFilterChange = (patch: { year?: string; region?: string; sort?: SortKey }) => {
    if (patch.year !== undefined) setYear(patch.year);
    if (patch.region !== undefined) setRegion(patch.region);
    if (patch.sort !== undefined) setSort(patch.sort);
    setPage(1);
  };

  const handleReset = () => {
    setYear("全部");
    setRegion("全部");
    setSort("latest");
    setPage(1);
  };

  const handlePageChange = (nextPage: number) => {
    setPage(Math.min(Math.max(1, nextPage), totalPages));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!category) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="container-page flex-1 py-12 md:py-16">
          <EmptyState
            icon="ri-error-warning-line"
            title="分类不存在"
            description="你访问的分类可能已调整或链接输入有误，可以返回首页或从下方分类中重新选择。"
          >
            <Link
              to="/"
              className="focus-ring inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-home-5-line text-sm" aria-hidden="true" />
              返回首页
            </Link>
            <Link
              to="/search"
              className="focus-ring inline-flex items-center gap-1.5 rounded-md border border-background-300/80 bg-white px-4 py-2 text-xs font-medium text-foreground-700 transition-colors hover:border-secondary-300"
            >
              站内搜索
            </Link>
          </EmptyState>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            {categories.map((item) => (
              <Link
                key={item.key}
                to={`/category/${item.key}`}
                className="focus-ring inline-flex items-center gap-1.5 rounded-full border border-background-300/80 bg-white px-3 py-1.5 text-xs text-foreground-600 transition-colors hover:border-secondary-300 hover:text-secondary-700"
              >
                <i className={`${item.icon} text-sm`} aria-hidden="true" />
                {item.name}
              </Link>
            ))}
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <div className="container-page pt-4 md:pt-5">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: category.name }]} />
        </div>

        <section className="container-page pt-5 md:pt-6">
          <div className="flex flex-col gap-4 border-b border-background-200/80 pb-6 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <i className={`${category.icon} text-base`} aria-hidden="true" />
                </span>
                <p className="font-label text-[10px] font-semibold uppercase tracking-[0.2em] text-secondary-600">
                  Category
                </p>
              </div>
              <h1 className="mt-2 text-2xl font-semibold text-foreground-950 md:text-[32px]">
                {category.name}
              </h1>
              <p className="mt-2 max-w-2xl text-xs leading-relaxed text-foreground-500 md:text-sm">
                {category.description}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {categories.map((item) => (
                <Link
                  key={item.key}
                  to={`/category/${item.key}`}
                  className={`focus-ring inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-[11px] font-medium transition-colors md:text-xs ${
                    item.key === category.key
                      ? "border-primary-500 bg-primary-500 text-background-50"
                      : "border-background-300/80 bg-white text-foreground-600 hover:border-secondary-300 hover:text-secondary-700"
                  }`}
                >
                  {item.shortName}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="container-page pt-5">
          <FilterBar
            year={year}
            region={region}
            sort={sort}
            resultCount={results.length}
            onChange={handleFilterChange}
            onReset={handleReset}
          />
        </section>

        <section className="container-page pt-6 md:pt-8">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <p className="text-[11px] text-foreground-500 md:text-xs">
              当前筛选：
              <span className="text-foreground-800">
                {year === "全部" ? "全部年份" : `${year} 年`} ·{" "}
                {region === "全部" ? "全部地区" : region} ·{" "}
                {sort === "latest" ? "最新收录" : sort === "hot" ? "最热浏览" : "评分最高"}
              </span>
            </p>
            <p className="font-label text-[10px] uppercase tracking-[0.16em] text-foreground-400">
              第 {currentPage} / {totalPages} 页
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {Array.from({ length: PAGE_SIZE }).map((_, index) => (
                <SkeletonCard key={`skeleton-${index}`} />
              ))}
            </div>
          ) : pageItems.length === 0 ? (
            <EmptyState
              icon="ri-search-eye-line"
              title="没有符合条件的内容"
              description="当前筛选条件下暂无可展示的视频，可以重置筛选条件，或切换到其他分类继续浏览。"
            >
              <button
                type="button"
                onClick={handleReset}
                className="focus-ring inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line text-sm" aria-hidden="true" />
                重置筛选条件
              </button>
              <Link
                to="/category/all"
                className="focus-ring inline-flex items-center gap-1.5 rounded-md border border-background-300/80 bg-white px-4 py-2 text-xs font-medium text-foreground-700 transition-colors hover:border-secondary-300"
              >
                浏览全部视频
              </Link>
            </EmptyState>
          ) : (
            <div className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {pageItems.map((video) => (
                <VideoCard key={video.id} video={video} showCategory={category.key === "all"} />
              ))}
            </div>
          )}

          {!loading && pageItems.length > 0 ? (
            <Pagination current={currentPage} total={totalPages} onChange={handlePageChange} />
          ) : null}
        </section>

        <section className="container-page pt-10 md:pt-12">
          <div className="surface-card flex flex-col items-start gap-3 p-5 md:flex-row md:items-center md:justify-between md:p-6">
            <div>
              <h2 className="text-sm font-semibold text-foreground-900 md:text-base">
                没有找到想看的内容？
              </h2>
              <p className="mt-1 text-[11px] leading-relaxed text-foreground-500 md:text-xs">
                可以尝试站内搜索，按标题、简介、导演或标签检索全部视频内容。
              </p>
            </div>
            <Link
              to="/search"
              className="focus-ring inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-xs font-semibold text-primary-950 transition-colors hover:bg-accent-400"
            >
              <i className="ri-search-line text-sm" aria-hidden="true" />
              前往搜索
            </Link>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}